import { Job } from './../../types/job';
import { AxiosRequestConfig, AxiosError } from 'axios';
import axiosInstance from '../axios';
import { API_BASE_URL } from '../../constants';
import { Answer } from '../../types/answer';
import { Question } from '../../types/question';
import { checkIfAxiosErrorAndUnauthenticatedAndThrowError, ServiceResponse } from '../common';
import { ANSWER_QUESTION_ENDPOINT, GET_QUESTIONS_BY_JOB_ID_ENDPOINT } from './constants';
import {
  JobPositionResponse,
  LegacyAPIResponse,
  GetJobResponse,
  ApplyToJobResponse,
  JobApplicationPayload,
} from '../api-response/legacy-response';
import { JobPosition } from '../../types/job-position';

export class JobAPI {
  jobServiceRoute = API_BASE_URL;
  config: AxiosRequestConfig = {};

  constructor() {
    this.config = { withCredentials: true };
  }

  getQuestionsByJobIdAndApplicationId = async (
    jobId: number,
    applicationId: number,
  ): Promise<Question[]> => {
    const endpoint = GET_QUESTIONS_BY_JOB_ID_ENDPOINT(jobId);
    const params = new URLSearchParams([['applicationId', applicationId.toString()]]);

    try {
      const result = await axiosInstance.get<ServiceResponse<Question[]>>(
        `${this.jobServiceRoute}/${endpoint}`,
        {
          ...this.config,
          params,
        },
      );

      if (result.data.code === 200) {
        return result.data.payload;
      }
    } catch (e) {
      checkIfAxiosErrorAndUnauthenticatedAndThrowError(e, 'Job');
    }

    throw new Error('common.somethingWentWrong');
  };

  answerQuestion = async (
    questionId: number,
    text: string,
    applicationId: number,
  ): Promise<Answer> => {
    const endpoint = ANSWER_QUESTION_ENDPOINT;

    try {
      const result = await axiosInstance.post<ServiceResponse<Answer>>(
        `${this.jobServiceRoute}/${endpoint}`,
        {
          questionId,
          text,
          applicationId,
        },
        this.config,
      );
      if (result.data.code === 200) {
        return result.data.payload;
      }
    } catch (e) {
      checkIfAxiosErrorAndUnauthenticatedAndThrowError(e, 'Job');
    }

    throw new Error('common.somethingWentWrong');
  };

  getJobPositions = async (searchQuery: string): Promise<JobPosition[]> => {
    try {
      const result = await axiosInstance.get<JobPositionResponse>(
        `${this.jobServiceRoute}/job/job-positions/datalist`,
        {
          params: searchQuery
            ? {
                q: searchQuery,
              }
            : {},
          headers: this.config.headers,
        },
      );

      return result.data.payload;
    } catch (e) {
      throw new Error('apiResponse.couldNotGetJobPositions');
    }
  };

  getJobListingWithUUID = async (uuid: string): Promise<Job> => {
    try {
      const result = await axiosInstance.get<GetJobResponse>(
        `${this.jobServiceRoute}/job/public/listing/detail/${uuid}`,
        this.config,
      );

      if (result.status === 200) {
        const loadedJob = result.data.payload as Job;
        const latLon = loadedJob.location?.split(',');

        if (latLon) {
          return {
            ...loadedJob,
            lat: parseFloat(latLon[0]),
            lng: parseFloat(latLon[1]),
          };
        }

        return loadedJob;
      }
    } catch (e) {
      checkIfAxiosErrorAndUnauthenticatedAndThrowError(e, 'Job');

      const error = e as AxiosError<GetJobResponse>;

      if (error.response?.status === 404) {
        throw new Error('apiResponse.couldNotGetJob');
      }

      throw new Error((error.response?.data as LegacyAPIResponse).payload.message);
    }

    throw new Error('apiResponse.couldNotGetJob');
  };

  applyToJob = async (jobListingId: number): Promise<JobApplicationPayload> => {
    try {
      const result = await axiosInstance.post<ApplyToJobResponse>(
        `${this.jobServiceRoute}/job/application`,
        { listingId: jobListingId },
        this.config,
      );

      return result.data.payload.application;
    } catch (e) {
      checkIfAxiosErrorAndUnauthenticatedAndThrowError(e, 'Job');
      const error = e as AxiosError<ApplyToJobResponse>;

      throw new Error(error.response?.data.message ?? 'common.somethingWentWrong');
    }
  };
}
